/**
 * The browser mirror of `server/format.py`. Byte-identical output is the whole
 * contract: a credential signed in Python is verified here with no network in
 * between, so anything this file encodes differently is a signature the server
 * never made.
 *
 * Two things carry that contract. Encoding is RFC 8949 core-deterministic,
 * which agrees with Python's `canonical=True` for the integer keys on this
 * wire, so map key order never depends on insertion order. It is not the same
 * rule: RFC 8949 §4.2.1 sorts encoded keys bytewise and cbor2's canonical mode
 * sorts length-first per RFC 7049 §3.9. They coincide on every non-negative
 * integer, which is every key this protocol has, and they diverge on negative
 * ones — so a key outside that range needs the vectors re-pinned rather than
 * assumed. And decoding is strict in the same places: every field's
 * type and length is checked, a CBOR `true` is not an integer, and an unknown
 * key is an error rather than something to ignore. A tolerant decoder here and
 * a strict one there is how two implementations drift apart.
 */
import { decode, decodeSequence, encode, getEncoded } from "cbor2";
import { sortCoreDeterministic } from "cbor2/sorts";
import {
  ATTR,
  CHALLENGE_DOMAIN,
  ERRAND_DOMAIN,
  ERRAND_TEXT_MAX,
  FORMAT_VERSION,
  HISTORY_DOMAIN,
  LOGOUT_DOMAIN,
  NONCE_LEN,
  PORTRAIT_DOMAIN,
  PRESENTATION_DOMAIN,
  RECEIPT_DOMAIN,
  RECEIPT_NOTE_MAX,
  RENEWAL_DOMAIN,
  SIGNATURE_LEN,
  STATUS_DOMAIN,
} from "./constants";

const ENCODE_OPTIONS = { sortKeys: sortCoreDeterministic };
// preferMap keeps a CBOR map a Map even when every key is text, so the shape a
// decoder sees never depends on what the keys happen to be.
//
// rejectFloats because major type 7 reaches JavaScript as an ordinary number
// and `Number.isInteger(2)` cannot tell it from major type 0: a presentation
// with its version as a CBOR half and its timestamp as a double decoded
// cleanly here and raised `presentation key 0 must be an integer` in Python.
// Every number on this wire is an integer, so the rule belongs at the CBOR
// layer where it covers the fields no `takeInt` looks at as well.
const DECODE_OPTIONS = { preferMap: true, rejectDuplicateKeys: true, rejectFloats: true };

const CREDENTIAL_REQUIRED_KEYS = [1, 2, 3];
const CREDENTIAL_KEYS = new Set(CREDENTIAL_REQUIRED_KEYS);
const PRESENTATION_REQUIRED_KEYS = [0, 1, 2, 3, 4, 5];
const PRESENTATION_KEYS = new Set([...PRESENTATION_REQUIRED_KEYS, 6]);
const STATUS_LIST_REQUIRED_KEYS = [1, 2, 3, 4];
const STATUS_LIST_KEYS = new Set(STATUS_LIST_REQUIRED_KEYS);
const ERRAND_REQUIRED_KEYS = [1, 2, 3, 4, 5];
const ERRAND_KEYS = new Set(ERRAND_REQUIRED_KEYS);
const RECEIPT_REQUIRED_KEYS = [1, 2, 3, 4, 5, 6, 7, 8];
const RECEIPT_KEYS = new Set(RECEIPT_REQUIRED_KEYS);

export type Attributes = Map<number, unknown>;

export interface Credential {
  attributesBytes: Uint8Array;
  signature: Uint8Array;
  kid: number;
}

export interface PresentationFields {
  credentialBytes: Uint8Array;
  timestamp: number;
  nonce: Uint8Array;
  mode: number;
  errandBytes?: Uint8Array;
}

export interface Presentation extends PresentationFields {
  version: number;
  signature: Uint8Array;
}

export interface StatusList {
  issuedAt: number;
  /** Bit i, least significant first, is 1 when credential i is revoked. */
  bitmap: Uint8Array;
  kid: number;
  signature: Uint8Array;
}

export interface ErrandFields {
  /** The grantor's portrait-less credential, as the issuer signed it. */
  credentialBytes: Uint8Array;
  text: string;
  /** The UH identifier of the one person who may present this errand. */
  bearer: string;
  expiresAt: number;
}

export interface Errand extends ErrandFields {
  signature: Uint8Array;
}

export interface ReceiptFields {
  time: number;
  /** "" when the verifier could not read who was presented. */
  subject: string;
  name: string;
  /** "ok" or the refusal reason. */
  result: string;
  mode: number;
  nonce: Uint8Array;
  note: string;
}

export interface Receipt extends ReceiptFields {
  signature: Uint8Array;
}

// "attributes" is plural; every other noun on this wire is singular. The same
// line is in `server/format.py`, so the two sides word their refusals alike.
const verbFor = (what: string): string => (what === "attributes" ? "are" : "is");

/**
 * How many bytes follow the first complete CBOR document in `raw`.
 *
 * Only ever called once `decode` has already refused the input, so the second
 * parse costs nothing on the path that matters. `decodeSequence` is a lazy
 * generator: pulling one item parses the first document and stops, which is
 * what makes this work on a tail that is itself malformed. `boxed` is what
 * lets `getEncoded` report a span for a document that decoded to a primitive.
 */
function trailingByteCount(raw: Uint8Array): number {
  try {
    const options = { ...DECODE_OPTIONS, boxed: true, saveOriginal: true };
    const first = decodeSequence(raw, options)[Symbol.iterator]().next().value;
    return raw.length - (getEncoded(first)?.length ?? raw.length);
  } catch {
    return 0;
  }
}

/**
 * The mirror of `_loads_exact` in `server/format.py`: decode exactly one CBOR
 * document, insist the input ends there, and refuse a map that could have been
 * written more than one way.
 *
 * Trailing bytes and duplicate keys both come for free on this side, from
 * cbor2's "Extra data in input" and `rejectDuplicateKeys`. An indefinite-length
 * map does not: cbor2 decodes one happily, and Python now refuses it, so the
 * check below is written here by hand rather than reached for in
 * `rejectStreaming`, which would also refuse indefinite-length strings that
 * Python accepts. The rule has to be the same rule on both sides.
 */
function decodeCBOR(raw: Uint8Array, what: string): unknown {
  const verb = verbFor(what);
  let value: unknown;
  try {
    value = decode(raw, DECODE_OPTIONS);
  } catch (cause) {
    const trailing = trailingByteCount(raw);
    if (trailing > 0) {
      const plural = trailing === 1 ? "" : "s";
      throw new Error(`${what} ${verb} followed by ${trailing} trailing byte${plural}`, {
        cause,
      });
    }
    throw new Error(`${what} ${verb} not valid CBOR`, { cause });
  }
  if (value instanceof Map) {
    // A Map does not prove the document opened with a map head. cbor2 resolves
    // some tags to the value inside them — tag 55799, self-described CBOR,
    // hands back the Map — so a tag-wrapped envelope used to pass every check
    // here while Python refused it. Python's mirror of this reads the same head
    // byte and words the refusal the same way.
    if (raw[0] < 0xa0 || raw[0] > 0xbf) throw new Error(`${what} must be a CBOR map`);
    if (raw[0] === 0xbf) throw new Error(`${what} ${verb} an indefinite-length CBOR map`);
  }
  return value;
}

function requireMap(value: unknown, what: string): Map<unknown, unknown> {
  if (!(value instanceof Map)) throw new Error(`${what} must be a CBOR map`);
  return value;
}

/**
 * Parse strictly: no key may be missing, and no key may be a stranger.
 *
 * Tolerating unknown keys would give one logical message many byte forms, and a
 * padded envelope still verifies because the signature covers the inner bytes.
 * Forward compatibility is what FORMAT_VERSION is for.
 */
function checkKeys(
  envelope: Map<unknown, unknown>, what: string, required: number[], known: Set<number>,
): void {
  const missing = required.filter((key) => !envelope.has(key));
  if (missing.length > 0) {
    throw new Error(`${what} is missing required keys [${missing.join(", ")}]`);
  }
  const unknown = [...envelope.keys()].filter(
    (key) => typeof key !== "number" || !known.has(key),
  );
  if (unknown.length > 0) {
    throw new Error(`${what} carries unknown keys [${unknown.map(String).sort().join(", ")}]`);
  }
}

function takeInt(envelope: Map<unknown, unknown>, key: number, what: string): number {
  const value = envelope.get(key);
  // A CBOR true is not a number on this wire, which `typeof` already settles
  // here; Python needs the extra `isinstance(value, bool)` because bool
  // subclasses int there.
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new Error(`${what} key ${key} must be an integer`);
  }
  return value;
}

function takeBytes(
  envelope: Map<unknown, unknown>, key: number, what: string, length?: number,
): Uint8Array {
  const value = envelope.get(key);
  if (!(value instanceof Uint8Array)) {
    throw new Error(`${what} key ${key} must be a byte string`);
  }
  if (length !== undefined && value.length !== length) {
    throw new Error(`${what} key ${key} must be ${length} bytes, got ${value.length}`);
  }
  return value;
}

function takeText(
  envelope: Map<unknown, unknown>, key: number, what: string, maxBytes?: number,
): string {
  const value = envelope.get(key);
  if (typeof value !== "string") {
    throw new Error(`${what} key ${key} must be a text string`);
  }
  if (maxBytes !== undefined) {
    // `.length` counts UTF-16 code units, which is neither the characters a
    // person typed nor the bytes the QR carries; the cap is about the bytes.
    const size = new TextEncoder().encode(value).length;
    if (size > maxBytes) {
      throw new Error(`${what} key ${key} must be at most ${maxBytes} bytes of UTF-8, got ${size}`);
    }
  }
  return value;
}

export function encodeAttributes(attributes: Attributes): Uint8Array {
  return encode(attributes, ENCODE_OPTIONS);
}

export function decodeAttributes(raw: Uint8Array): Attributes {
  return requireMap(decodeCBOR(raw, "attributes"), "attributes") as Attributes;
}

export function encodeCredential(
  attributesBytes: Uint8Array, signature: Uint8Array, kid: number,
): Uint8Array {
  return encode(
    new Map<number, unknown>([[1, attributesBytes], [2, signature], [3, kid]]),
    ENCODE_OPTIONS,
  );
}

export function decodeCredential(raw: Uint8Array): Credential {
  const credential = requireMap(decodeCBOR(raw, "credential"), "credential");
  checkKeys(credential, "credential", CREDENTIAL_REQUIRED_KEYS, CREDENTIAL_KEYS);
  return {
    attributesBytes: takeBytes(credential, 1, "credential"),
    signature: takeBytes(credential, 2, "credential", SIGNATURE_LEN),
    kid: takeInt(credential, 3, "credential"),
  };
}

export function encodePresentation(
  fields: PresentationFields & { signature: Uint8Array },
): Uint8Array {
  const envelope = new Map<number, unknown>([
    [0, FORMAT_VERSION],
    [1, fields.credentialBytes],
    [2, fields.timestamp],
    [3, fields.nonce],
    [4, fields.mode],
    [5, fields.signature],
  ]);
  if (fields.errandBytes != null) envelope.set(6, fields.errandBytes);
  return encode(envelope, ENCODE_OPTIONS);
}

export function decodePresentation(raw: Uint8Array): Presentation {
  const envelope = requireMap(decodeCBOR(raw, "presentation"), "presentation");
  checkKeys(envelope, "presentation", PRESENTATION_REQUIRED_KEYS, PRESENTATION_KEYS);
  const version = takeInt(envelope, 0, "presentation");
  if (version !== FORMAT_VERSION) {
    throw new Error(`unsupported presentation version ${version}`);
  }
  return {
    version,
    credentialBytes: takeBytes(envelope, 1, "presentation"),
    timestamp: takeInt(envelope, 2, "presentation"),
    nonce: takeBytes(envelope, 3, "presentation", NONCE_LEN),
    mode: takeInt(envelope, 4, "presentation"),
    signature: takeBytes(envelope, 5, "presentation", SIGNATURE_LEN),
    errandBytes: envelope.has(6) ? takeBytes(envelope, 6, "presentation") : undefined,
  };
}

/**
 * Index 5 is CBOR null when there is no errand. `undefined` would encode as
 * 0xf7 and sign bytes no Python verifier accepts, so the coalesce is the
 * protocol, not a convenience.
 */
export function presentationSigningInput(fields: PresentationFields): Uint8Array {
  return encode([
    PRESENTATION_DOMAIN,
    fields.credentialBytes,
    fields.timestamp,
    fields.nonce,
    fields.mode,
    fields.errandBytes ?? null,
  ], ENCODE_OPTIONS);
}

export function decodeStatusList(raw: Uint8Array): StatusList {
  const envelope = requireMap(decodeCBOR(raw, "status list"), "status list");
  checkKeys(envelope, "status list", STATUS_LIST_REQUIRED_KEYS, STATUS_LIST_KEYS);
  return {
    issuedAt: takeInt(envelope, 1, "status list"),
    bitmap: takeBytes(envelope, 2, "status list"),
    kid: takeInt(envelope, 3, "status list"),
    signature: takeBytes(envelope, 4, "status list", SIGNATURE_LEN),
  };
}

/** The mirror of `status_list_signing_input`: fixed shape, own domain separator. */
export function statusListSigningInput(issuedAt: number, bitmap: Uint8Array): Uint8Array {
  return encode([STATUS_DOMAIN, issuedAt, bitmap], ENCODE_OPTIONS);
}

/**
 * What the device signs to renew: the issuer's challenge and the credential
 * being renewed, under their own domain separator so a renewal signature can
 * never be replayed as a presentation or the other way round.
 */
export function renewalSigningInput(challenge: Uint8Array, credentialBytes: Uint8Array): Uint8Array {
  return encode([RENEWAL_DOMAIN, challenge, credentialBytes], ENCODE_OPTIONS);
}

/** What the device key signs to put up a portrait. The digest is of the exact
 *  bytes sent, so the signature covers the picture and nothing else. */
export function portraitSigningInput(
  challenge: Uint8Array, credentialBytes: Uint8Array, digest: Uint8Array,
): Uint8Array {
  return encode([PORTRAIT_DOMAIN, challenge, credentialBytes, digest], ENCODE_OPTIONS);
}

export function encodeErrand(fields: Errand): Uint8Array {
  return encode(new Map<number, unknown>([
    [1, fields.credentialBytes],
    [2, fields.text],
    [3, fields.bearer],
    [4, fields.expiresAt],
    [5, fields.signature],
  ]), ENCODE_OPTIONS);
}

/**
 * The text cap lives here as well as in the wallet's form, because any
 * program holding a device key can write an errand, and a cap only the honest
 * wallet applies is not a cap.
 */
export function decodeErrand(raw: Uint8Array): Errand {
  const envelope = requireMap(decodeCBOR(raw, "errand"), "errand");
  checkKeys(envelope, "errand", ERRAND_REQUIRED_KEYS, ERRAND_KEYS);
  return {
    credentialBytes: takeBytes(envelope, 1, "errand"),
    text: takeText(envelope, 2, "errand", ERRAND_TEXT_MAX),
    bearer: takeText(envelope, 3, "errand"),
    expiresAt: takeInt(envelope, 4, "errand"),
    signature: takeBytes(envelope, 5, "errand", SIGNATURE_LEN),
  };
}

/**
 * What the grantor's device key signs. The lite credential is inside, as
 * transmitted, so an errand cannot be moved onto another grantor's credential.
 */
export function errandSigningInput(fields: ErrandFields): Uint8Array {
  return encode([
    ERRAND_DOMAIN, fields.credentialBytes, fields.text, fields.bearer, fields.expiresAt,
  ], ENCODE_OPTIONS);
}

export function encodeReceipt(fields: Receipt): Uint8Array {
  return encode(new Map<number, unknown>([
    [1, fields.time],
    [2, fields.subject],
    [3, fields.name],
    [4, fields.result],
    [5, fields.mode],
    [6, fields.nonce],
    [7, fields.note],
    [8, fields.signature],
  ]), ENCODE_OPTIONS);
}

export function decodeReceipt(raw: Uint8Array): Receipt {
  const envelope = requireMap(decodeCBOR(raw, "receipt"), "receipt");
  checkKeys(envelope, "receipt", RECEIPT_REQUIRED_KEYS, RECEIPT_KEYS);
  return {
    time: takeInt(envelope, 1, "receipt"),
    subject: takeText(envelope, 2, "receipt"),
    name: takeText(envelope, 3, "receipt"),
    result: takeText(envelope, 4, "receipt"),
    mode: takeInt(envelope, 5, "receipt"),
    nonce: takeBytes(envelope, 6, "receipt", NONCE_LEN),
    note: takeText(envelope, 7, "receipt", RECEIPT_NOTE_MAX),
    signature: takeBytes(envelope, 8, "receipt", SIGNATURE_LEN),
  };
}

/**
 * Its own separator because the verifier's device key also signs
 * presentations, errands and renewals, and a receipt must never pass as one.
 */
export function receiptSigningInput(fields: ReceiptFields): Uint8Array {
  return encode([
    RECEIPT_DOMAIN, fields.time, fields.subject, fields.name, fields.result, fields.mode,
    fields.nonce, fields.note,
  ], ENCODE_OPTIONS);
}

export function encodeChallengeQr(nonce: Uint8Array): Uint8Array {
  return encode([CHALLENGE_DOMAIN, nonce], ENCODE_OPTIONS);
}

/**
 * The nonce of a strong-mode challenge, or a throw for any other QR.
 *
 * The holder points the camera at whatever is in front of it, so a
 * presentation, an errand or a poster has to be refused here rather than have
 * its bytes signed as a nonce. The head byte is read before the decoded value,
 * as `decodeCBOR` does for maps: cbor2 resolves some tags to the value inside
 * them, and an indefinite-length array decodes to the same array. 0x82 is the
 * one head canonical encoding gives this array, and `decode_challenge_qr` in
 * `server/format.py` reads the same byte.
 */
export function decodeChallengeQr(raw: Uint8Array): Uint8Array {
  const value = decodeCBOR(raw, "challenge");
  if (raw[0] !== 0x82 || !Array.isArray(value)) {
    throw new Error("challenge must be a two-element CBOR array");
  }
  const [domain, nonce] = value as unknown[];
  if (domain !== CHALLENGE_DOMAIN) {
    throw new Error("challenge does not carry the challenge domain separator");
  }
  if (!(nonce instanceof Uint8Array)) throw new Error("challenge nonce must be a byte string");
  if (nonce.length !== NONCE_LEN) {
    throw new Error(`challenge nonce must be ${NONCE_LEN} bytes, got ${nonce.length}`);
  }
  return nonce;
}

/**
 * The renewal's shape under its own separator, so a signature that answered a
 * renewal challenge cannot be replayed to read a history, or the reverse.
 */
export function historySigningInput(
  challenge: Uint8Array, credentialBytes: Uint8Array,
): Uint8Array {
  return encode([HISTORY_DOMAIN, challenge, credentialBytes], ENCODE_OPTIONS);
}

/**
 * The same shape again under the logout separator: what the device signs to
 * have the issuer revoke its own credential. The separator is what keeps a
 * signature that renews from being replayed as one that revokes.
 */
export function logoutSigningInput(
  challenge: Uint8Array, credentialBytes: Uint8Array,
): Uint8Array {
  return encode([LOGOUT_DOMAIN, challenge, credentialBytes], ENCODE_OPTIONS);
}

/**
 * Bit `index` of the list, least significant bit of each byte first, the
 * order `Registry.revoked_bitmap` writes. `"beyond"` means the credential was
 * issued after the list was signed, so the list says nothing about it, which
 * is not the same answer as `false`.
 *
 * An index that is not a non-negative integer throws: `bitmap[-1]` is
 * undefined and would read as a clear bit, a clean answer about a credential
 * that cannot exist. Division rather than `>>` because the shift works on 32
 * bits and an index is any safe integer.
 */
export function isRevoked(bitmap: Uint8Array, index: number): boolean | "beyond" {
  if (!Number.isSafeInteger(index) || index < 0) {
    throw new RangeError(`status index must be a non-negative integer, got ${index}`);
  }
  if (index >= bitmap.length * 8) return "beyond";
  return ((bitmap[Math.floor(index / 8)] >> (index % 8)) & 1) === 1;
}

export { ATTR };
